/**
 * Small text/markup helpers shared by the map popup code in main.ts and the planned-works
 * wording in plannedPopup.ts. No Leaflet, no DOM -- unit tested via plannedPopup's tests.
 */

export const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', { ...TIME_OPTS, day: '2-digit', month: 'short' });
}

// Small inline popup icons shared by main.ts's own meta rows and plannedPopup.ts's.
export const ICON_CLOCK = '<circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path>';
export const ICON_PEOPLE_FILL = '<circle cx="9" cy="8" r="3.2"></circle><path d="M2.5 19v-1.4a6.5 6.5 0 0 1 13 0V19c0 .6-.5 1-1 1h-11c-.6 0-1-.4-1-1Z"></path><path d="M17 5.2a3 3 0 0 1 0 5.8c2.7.7 4.5 3 4.5 5.8V19h-4v-1.4c0-2.5-.9-4.7-2.5-6.2A4.9 4.9 0 0 0 17 5.2Z"></path>';

export function outlineIcon(pathData: string, stroke = '#0B1F3B', size = 13): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" width="${size}" height="${size}">${pathData}</svg>`;
}
export function filledIcon(pathData: string, fill: string, size = 13): string {
  return `<svg viewBox="0 0 24 24" fill="${fill}" aria-hidden="true" width="${size}" height="${size}">${pathData}</svg>`;
}

export function formatDuration(minutes: number): string {
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = Math.round(minutes % 60);
  if (days > 0) return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
  if (hours > 0) return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  return `${mins}m`;
}

/** Consistent-height meta row -- Option A from the card mockup: every incident of a
 * given status shows the same rows in the same order, "Not published" standing in
 * for a field this operator doesn't report rather than the row just vanishing.
 * `icon`, when given, is small inline SVG markup shown in its own grey circle to
 * the row's left (screen_mocks.png's own alignment for these rows) -- a flat
 * sibling of the label span, not a wrapper around it, so the label's closing tag
 * still sits immediately before the value's opening tag (existing tests match on
 * that exact adjacency); .popup-meta__label's own flex-grow is what pushes the
 * value to the row's right edge. */
export function metaRow(label: string, value: string, placeholder = false, icon = ''): string {
  const valueClass = placeholder ? 'popup-meta__value popup-meta__value--placeholder' : 'popup-meta__value';
  const iconHtml = icon ? `<span class="popup-meta__icon">${icon}</span>` : '';
  return `<div class="popup-meta__row">${iconHtml}<span class="popup-meta__label">${label}</span><span class="${valueClass}">${value}</span></div>`;
}
